import {Injectable} from "@angular/core";
import {IrbModelsEvents} from '@irb/irb-models/irb-models.events';
import {AppService} from '@irb/app.service';
import {MmcInventoryComponent} from "@irb/irb-models/model-market-center/mmc-inventory/mmc-inventory.component";
import { AnalyticsService } from "@irb/irb-common/analytics/analytics.service";

@Injectable({
    providedIn: "root"
})
export class MmcInventoryColumnsService {
    mapAssetTypeReversed = new Map<string, string>();
    assetTypeList: Array<string> = [];
    mapModelProvidersReversed = new Map<string, string>();
    modelProvidersList: Array<string> = [];
    mapRiskToleranceReversed = new Map<string, string>();
    riskToleranceList: Array<string> = [];
    mapAssetClassReversed = new Map<string, string>();
    assetClassList: Array<string> = [];
    modelMarketIdList: Array<string> = [];
    modelProvidersListUnaltered: any[] = [];
    informaPSNIdList: Array<string> = [];
    mmcInventoryComponent: MmcInventoryComponent;
    mapAllocationChanges = new Map<string, string>();
    usedForList: Array<string> = [];
    constructor(private as: AppService, private ats: AnalyticsService) {
        this.mapAllocationChanges.set('+/- Annually', 'ANNUALLY');
        this.mapAllocationChanges.set('+/- Quarterly', 'QUARTERLY');
        this.mapAllocationChanges.set('+/- Monthly', 'MONTHLY');
        this.mapAllocationChanges.set('Continuous/as needed', 'CONTINUOUS');
        this.mapAllocationChanges.set('Never', 'NEVER');
        this.mapAllocationChanges.set('None', 'NONE');
    }

    init(mmcInventoryComponent: MmcInventoryComponent) {
        this.mmcInventoryComponent = mmcInventoryComponent;
    }
    getFrameworkComponents() {
        return {};
    }

    getProviderColumnDefs() {       // parent: any
        let cols = this.getAllColumnDefs();
        cols[1].children.splice(4,1);               // removing In Use Subscription
        cols[2].children.shift();   // removing finmason finscore column
        cols[1].children.splice(1, 1);  // removing Manager column
        cols[0] = this.getProviderAdminFirstColumn();
        return cols;
    }

    getAdminColumnDefs() {       // parent: any
        let cols = this.getAllColumnDefs();
        cols[1].children.splice(4,1);   // removing In Use Subscription
        cols[2].children.shift();       // removing finmason finscore column
        cols[0] = this.getProviderAdminFirstColumn();
        return cols;
    }

    getAdvisorColumnDefs() {       // parent: any
        let cols = this.getAllColumnDefs();
        cols[0] = this.getAdvisorFirstColumn();
        cols[1].children.splice(3,1); // removing Update Status column
        cols[1].children.splice(2,1); // removing Status column
        cols[2].children.shift();   // removing finmason finscore column
        cols.splice(4,1) // removing "Schwab Data" section
        return cols;
    }

    getAdvisorFirstColumn() {
        return {
            headerName: 'Select All',
            colId:'selectAll',
            headerComponent: 'customHeader',
            cellRenderer: 'customHeader',
            cellRendererParams: { parentObject: this.mmcInventoryComponent, type: 'rowCheckBox' },
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            suppressSizeToFit: true,
            headerComponentParams: { parentObject: this.mmcInventoryComponent, type: 'justAddEvents'},
            floatingFilterComponent: "customHeader",
            floatingFilterComponentParams: { parentObject: this.mmcInventoryComponent, type: "NoClearAllBtn"},
            suppressFloatingFilterButton: true,
            pinned: 'left',
            suppressMovable: true,
            lockPosition: true,
            lockVisible: true,
            sortable: false,
            filter: true,
            resizable: true,
            minWidth: 24,
            width: 24,
            maxWidth:24,
            cellStyle: { 'padding': '5px 3px 0 2px' },
            headerClass: 'customHeaderClassSelectAll',
            cellClass: 'selectAllCheckBoxRecord'
        }
    }

    getAllColumnDefs() {    // parent: any
        const columns: any = [
            { // 0
                headerName: "",
                colId: "selectAll",
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                suppressSizeToFit: true,
                floatingFilterComponent: "customHeader",
                floatingFilterComponentParams: { parentObject: this.mmcInventoryComponent, type: "NoClearAllBtn" },
                suppressFloatingFilterButton: true,
                pinned: "left",
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                sortable: false,
                filter: true,
                minWidth: 24,
                width: 24,
                cellStyle: { padding: "2px 2px 2px 2px", border: 0 },
            },
            { // 1
                headerName: "Model Overview",
                children: [
                    { // 0
                        headerName: "Model Name",
                        field: "modelMarketName",
                        pinned: "left",
                        lockPosition: true,
                        suppressFiltersToolPanel: true,
                        lockVisible: true,
                        minWidth: 140,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            sendRowIndex: true,
                            link: (params: any) => {
                                return (params.value != null && params.value.length > 0);
                            },
                            onClick: ((modelData: any, rowIndex: any) => {
                                this.ats.trackClickEvent("md_mmc_detail");

                                if (this.mmcInventoryComponent && this.mmcInventoryComponent.roles && this.mmcInventoryComponent.roles.MMC_ADVISOR) {
                                    this.mmcInventoryComponent.openAdvisorModelDetail(modelData, rowIndex);
                                } else {
                                    let eventData: any = {
                                        type: 'editMmcModel',
                                        data: modelData
                                    };
                                    this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MMC_MODEL, eventData);
                                }
                            })
                        },
                        filter: 'agTextColumnFilter',
                        suppressFloatingFilterButton: true,
                        filterParams: {
                            clearButton: true,
                            debounceMs: 1000,
                            maxNumConditions: 1,
                        },
                    },
                    { // 1
                        headerName: "Manager",
                        field: "modelProviderName",
                        pinned: "left",
                        lockPosition: true,
                        suppressFiltersToolPanel: true,
                        lockVisible: true,
                        minWidth: 80,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: this.mmcInventoryComponent.roles.MMC_ADMIN ? {
                            link: (params: any) => {
                                return (params.value != null && params.value.length > 0);
                            },
                            onClick: (function (modelData: any) {
                                const firmId = modelData && modelData.modelProviderId;
                                const eventData: any = {
                                    type: 'editFirmInfo',
                                    data: {firmId: firmId}
                                };
                                this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_FIRM_INFO, eventData);
                            }).bind(this)
                        } : {},
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.modelProvidersList);
                            },
                            valueFormatter: (params: any) => {
                                const id = this.mapModelProvidersReversed.get(params.value);
                                //work-around duplicate names: stripping id, added previously
                                return params.value.endsWith(' (' + id + ')') ? (params.value.substring(0, params.value.length - id.length-3))?.trim() : params.value?.trim();
                                },
                            refreshValuesOnOpen: true,
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: false,
                            cellHeight: 30,
                            closeOnApply: true 
                        }
                    },
                    { // 2
                        headerName: "Status",
                        field: "status",
                        pinned: "left",
                        lockPosition: true,
                        lockVisible: true,
                        suppressFiltersToolPanel: true,
                        minWidth: 80,
                        maxWidth: 140,
                        cellRenderer: 'cellRendererFormatter',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(['Published','Unpublished']);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true
                        }
                    },
                    { // 3
                        headerName: "Update Status",
                        field: "updateStatus",
                        pinned: "left",
                        lockPosition: true,
                        suppressFiltersToolPanel: true,
                        lockVisible: true,
                        cellRendererSelector: (params: any) => {
                          if (params && params.data && params.data.updateStatus && params.data.updateStatus === "FAILED") {
                            return {
                              component: "cellRendererFormatter",
                              params: { value: "Failed", type: "errorMsg" },
                            };
                          } else if (params && params.data && params.data.updateStatus && params.data.updateStatus === "PROCESSING") {
                            return {
                              component: "cellRendererFormatter",
                              params: { value: "Processing" },
                            };
                          } else if (params && params.data && params.data.updateStatus && params.data.updateStatus === "COMPLETED") {
                            return {
                              component: "cellRendererFormatter",
                              params: { value: "Completed" },
                            };
                          } else {
                            return "";
                          }
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(['Completed','Failed','Processing']);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true
                        },
                        minWidth: 80,
                        maxWidth: 140
                    },
                    { // 4
                        headerName: "Subscribed",
                        headerClass: "check-icon",
                        suppressFiltersToolPanel: true,
                        field: "isSubscribed",
                        pinned: "left",
                        headerComponentParams: {
                            irbSimpleHtmlHeaderToolTip: 'Subscribed',
                            irbSimpleHtmlHeader: `<div><img src="./assets/images/checkmark-circle.png" alt="Subscribed"></div>`
                        },
                        cellRendererSelector: (params: any) => {
                            const rowItem = params.data;
                            if(rowItem){
                                let template = `<div></div>`;
                                if (rowItem.isSubscribed) {
                                    template = `<div><img src="./assets/images/checkmark-circle.png" alt="Subscribed"></div>`;
                                }
                                return {
                                    component: "cellRendererFormatter",
                                    params: {
                                        value:rowItem.isSubscribed,
                                        template: template
                                    },
                                };
                            }
                        },
                        lockPosition: true,
                        lockVisible: true,
                        sortable: true,
                        filter:false,
                        minWidth: 32,
                        maxWidth: 32
                    },
                ]
            },
            { // 2
                headerName: "Overview Details",
                children: [
                    { // 0
                        headerName: 'FinMason FinScore',
                        field: "finscore",
                        suppressFiltersToolPanel: true,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'currency', displaySymbol: ' ' },
                        maxWidth: 165,
                        minWidth: 80,
                        headerComponentParams: {
                            irbColInfo: this.finMasonFinScoreInfo()
                        },
                        filter: 'agNumberColumnFilter',
                        filterParams: {
                            filterOptions:['inRange'],
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            maxNumConditions: 1,
                            cellHeight: 30,
                        }
                    },
                    { // 1
                        headerName: 'Building Block Style',
                        field: "buildingBlockStyle",
                        maxWidth: 220,
                        minWidth: 80,
                        valueFormatter: (params: any) => {
                            return this.titleCaseFormatter(params.value);
                        },
                        headerComponentParams: {
                            irbColInfo: this.buildingBlockStyleInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                const filterData = ['Active', 'Mixed', 'Passive', 'Smart Beta'];
                                if (this.usedForList.includes('CUSTOM'))
                                    filterData.unshift('None');
                                params.success(filterData);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 2
                        headerName: 'Used for',
                        field: "usedFor",
                        maxWidth: 175,
                        minWidth: 80,
                        valueGetter: (params: any) => {
                            return params.data && this.formatUsedFor(params.data.usedFor);
                        },
                        cellRenderer: 'cellRendererFormatter',
                        headerComponentParams: {
                            irbColInfo: this.usedForInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.formatUsedForArray(this.usedForList));
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 3
                        headerName: 'Tax-managed',
                        field: "isTaxManaged", // current true/false -> need Yes/No, formatBooleanToYesNo
                        maxWidth: 175,
                        minWidth: 80,
                        suppressFiltersToolPanel: true,
                        valueGetter: (params: any) => {
                            if (params.data && params.data.usedFor === 'custom')
                                return '--';
                            return params.data && this.formatBooleanToYesNo(params.data.isTaxManaged);
                        },
                        headerComponentParams: {
                            irbColInfo: this.isTaxManagedInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.yesNoFilterData());
                            },
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 4
                        headerName: 'Socially Responsible',
                        field: "isSociallyResponsible", // current true/false -> need Yes/No
                        maxWidth: 200,
                        minWidth: 80,
                        suppressFiltersToolPanel: true,
                        valueGetter: (params: any) => {
                            if (params.data.usedFor === 'custom' && params.data.isSociallyResponsible == null )
                                return '--';
                            return params.data && this.formatBooleanToYesNo(params.data.isSociallyResponsible);
                        },
                        headerComponentParams: {
                            irbColInfo: this.isSociallyResponsibleInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.yesNoFilterData());
                            },
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 5
                        headerName: 'Risk Category',
                        field: "riskTolerance.label",
                        maxWidth: 350,
                        minWidth: 80,
                        cellRenderer: 'cellRendererFormatter',
                        valueGetter: (params: any) => {
                            if (params.data.usedFor === 'custom' && params.data.riskTolerance.label === 'None' )
                                return '--';
                            return params.data.riskTolerance.label;
                        },
                        headerComponentParams: {
                            irbColInfo: this.riskToleranceInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            minWidth:250,
                            values: (params: any) => {
                                params.success(this.riskToleranceList);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 6
                        headerName: 'Asset Class',
                        field: "assetClass.label",
                        maxWidth: 300,
                        minWidth: 80,
                        valueGetter: (params: any) => {
                            if (params.data.usedFor === 'custom' && params.data.assetClass.label === 'None' )
                                return '--';
                            return params.data.assetClass.label;
                        },
                        cellRenderer: 'cellRendererFormatter',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.assetClassList);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: false,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 7
                        headerName: 'Weighted Yield',
                        field: "weightedYield",
                        suppressFiltersToolPanel: true,
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.weightedYield, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        headerComponentParams: {
                            irbColInfo: this.weightedYieldInfo()
                        },
                        maxWidth: 180
                    },
                    { // 8
                        headerName: '# Current Holdings',
                        field: "currentHoldings",
                        suppressFiltersToolPanel: true,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'currency', displaySymbol: ' ' },
                        maxWidth: 180
                    },
                    { // 9
                        headerName: 'Asset Type',
                        field: "assetType.label",
                        maxWidth: 180,
                        minWidth: 80,
                        valueGetter: (params: any) => {
                            if (params.data.usedFor === 'custom' && params.data.assetType.label === 'None' )
                                return '--';
                            return params.data.assetType.label;
                        },
                        cellRenderer: 'cellRendererFormatter',
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.assetTypeList);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: false,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 10
                        headerName: 'Suggested Minimum',
                        field: "suggestedMinimum",
                        cellRenderer: 'cellRendererFormatter',
                        // suppressFiltersToolPanel: true,
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '$' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.suggestedMinimum, { type: 'currency', decimalPosition: '2', displaySymbol: '$' }),
                        cellClass: 'irb-cell-content-right',
                        maxWidth: 200,
                        minWidth: 80,
                        headerComponentParams: {
                            irbColInfo: this.suggestedMinimumInfo()
                        },
                        floatingFilterComponent: 'irbInputFloatingFilterComponent',
                        filter: 'irbAgNumberColumnFilterComponent',

                    },
                ]
            },
            { // 3
                headerName: "Cost",
                children: [
                    { // 0
                        headerName: 'Strategist Fee',
                        field: "costBPs",
                        lockVisible: true,
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', displaySymbol: ' ' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.costBPs, { type: 'currency', displaySymbol: ' ' }),
                        cellClass: 'irb-cell-content-right',
                        maxWidth: 175,
                        minWidth: 80,
                        headerComponentParams: {
                            irbColInfo: this.costBPsInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.yesNoFilterData());
                            },
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 1
                        headerName: 'Weighted Net Expense Ratio',
                        field: "weightedNetExpenseRatio",
                        suppressFiltersToolPanel: true,
                        lockVisible: true,
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.weightedNetExpenseRatio, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        headerComponentParams: {
                            irbColInfo: this.weightedNetExpenseRatioInfo()
                        },
                        maxWidth: 245
                    },
                    { // 2
                        headerName: 'Estimated Transaction Fee',
                        field: "estimatedTransactionFee",
                        suppressFiltersToolPanel: true,
                        lockVisible: true,
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '$' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.estimatedTransactionFee, { type: 'currency', decimalPosition: '2', displaySymbol: '$' }),
                        cellClass: 'irb-cell-content-right',
                        headerComponentParams: {
                            irbColInfo: this.estimatedTransactionFeeInfo()
                        },
                        maxWidth: 225
                    }
                ]
            },
            { // 4
                headerName: "Schwab Data",
                headerGroupComponent: 'customHeaderGroup',
                suppressFiltersToolPanel: true,
                headerGroupComponentParams: {
                    irbColInfo: this.tdaDataInfo()
                },
                children: [
                    { // 0
                        headerName: 'AUM Per Model',
                        field: "aumPerModel",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '$' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.aumPerModel, { type: 'currency', decimalPosition: '2', displaySymbol: '$' }),
                        cellClass: 'irb-cell-content-right',
                        maxWidth: 200
                    },
                    { // 1
                        headerName: '# Accounts Per Model',
                        field: "accountsPerModel",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', displaySymbol: ' ' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.accountsPerModel, { type: 'currency', displaySymbol: ' ' }),
                        cellClass: 'irb-cell-content-right',
                        maxWidth: 220
                    },
                    { // 2
                        headerName: 'Morningstar ID',
                        field: "morningStarId",
                        valueFormatter: (params: any) => {
                            if (params.data.usedFor === 'custom')
                                return '--';
                            return params.value;
                        },
                        maxWidth: 200
                    },
                    { // 3
                        headerName: 'Informa PSN ID',
                        field: "sourceId",
                        valueFormatter: (params: any) => {
                            if (params.data.usedFor === 'custom')
                                return '--';
                            return params.value;
                        },
                        maxWidth: 200,
                        minWidth: 80,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.informaPSNIdList);
                            },
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }

                    },
                    { // 4
                        headerName: 'Perf Last Updated',
                        field: "performanceModifiedDate",
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            type: 'date', format: 'MM/dd/yyyy', displayETtext : false
                        },
                        //cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.performanceModifiedDate, { type: 'date', format: 'MM/dd/yyyy', displayETtext : false }),
                        maxWidth: 245
                    },
                    { // 5
                        headerName: 'Model ID',
                        field: "modelMarketId",
                        maxWidth: 150,
                        minWidth: 80,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                params.success(this.modelMarketIdList);
                            },
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    }
                ]
            },
            { // 5
                headerName: "Performance",
                headerGroupComponent: 'customHeaderGroup',
                suppressFiltersToolPanel: true,
                headerGroupComponentParams: {
                    irbColInfo: this.performanceInfo(),
                    maxWidth: '480px'
                },
                children: [
                    { // 0
                        headerName: '1MO',
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.me, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        field: "me",
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 1
                        headerName: 'T3',
                        field: "t3",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.t3, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 2
                        headerName: 'YTD',
                        field: "ytd",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.ytd, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 3
                        headerName: '1YR',
                        field: "t12",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.t12, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 4
                        headerName: '3YR',
                        field: "yr3",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.yr3, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 5
                        headerName: '5YR',
                        field: "yr5",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.yr5, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 6
                        headerName: '10YR',
                        field: "yr10",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.yr10, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 120
                    },
                    { // 7
                        headerName: 'As-of Inception Return',
                        field: "rorInception",
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.rorInception, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 200
                    },
                    { // 8
                        headerName: 'As-of Inception Date',
                        field: "inceptionDate",
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            type: 'date', format: 'MM/dd/yyyy', displayETtext : false
                        },
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 200
                    },
                    { // 9
                        headerName: 'GIPS-Compliant',
                        field: "gipsCompliant", // current true/false -> need Yes/No
                        valueGetter: (params: any) => {
                            if (params.data.usedFor === 'custom' && params.data.gipsCompliant == null)
                                return '--';
                            return params.data && this.formatBooleanToYesNo(params.data.gipsCompliant);
                        },
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 170
                    },
                    { // 10
                        headerName: 'Performance As-of',
                        field: "performanceAsOf", // data return performanceAsOf: 1577768400000 need mm/dd/yyyy
                        // cellRenderer: 'cellRendererFormatter',
                        // cellRendererParams: {
                        //     type: 'date', format: 'MM/dd/yyyy', displayETtext : false, convertWithNoDisplayETtext: true
                        // },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.performanceAsOf, { type: 'date', format: 'MM/dd/yyyy', displayETtext : false, convertWithNoDisplayETtext: true }),
                        toolPanelClass: "tp-grouped-ro",
                        maxWidth: 180
                    }
                ]
            },
            { // 6
                headerName: "Frequency",
                children: [
                    { // 0
                        headerName: 'Turnover',
                        field: "turnover", // need null -> '-'
                        // cellRenderer: 'cellRendererFormatter',
                        suppressFiltersToolPanel: true,
                        // cellRendererParams: { type: 'currency', decimalPosition: '2', displaySymbol: '%' },
                        cellRendererSelector: (params: any)=> this.cellRendererSelectorNumbers(params, params.data.turnover, { type: 'currency', decimalPosition: '2', displaySymbol: '%' }),
                        cellClass: 'irb-cell-content-right',
                        headerComponentParams: {
                            irbColInfo: this.turnoverdInfo()
                        },
                        maxWidth: 145
                    },
                    { // 1
                        headerName: 'Allocation Changes',
                        field: "allocationChanges",
                        maxWidth: 200,
                        minWidth: 80,
                        valueGetter: (params: any) => {
                            return params.data && this.formatAllocationChanges(params.data.allocationChanges);
                        },
                        cellRenderer: 'cellRendererFormatter',
                        headerComponentParams: {
                            irbColInfo: this.allocationChangesInfo()
                        },
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: (params: any) => {
                                const filterData = ['+/- Annually', '+/- Monthly','+/- Quarterly', 'Continuous/as needed', 'Never'];
                                if (this.usedForList.includes('CUSTOM'))
                                    filterData.unshift('None');
                                params.success(filterData);
                            },
                            showTooltips: true,
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            cellHeight: 30,
                            closeOnApply: true,
                            refreshValuesOnOpen: true
                        }
                    },
                    { // 2
                        headerName: 'Allocation Last Updated',
                        field: "publishOrAllocationUpdatedDate", // data return publishOrAllocationUpdatedDate: 1577768400000 need mm/dd/yyyy
                        maxWidth: 210,
                        minWidth: 80,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: {
                            type: 'date', format: 'MM/dd/yyyy', displayETtext : false
                        },
                        headerComponentParams: {
                            irbColInfo: this.allocationLastUpdInfo()
                        },
                        floatingFilterComponent: 'irbInputFloatingFilterComponent',
                        filter: 'rangeDatePicker'
                    }
                ]
            }
        ];
        return columns;
    }
    titleCaseFormatter(s: string) {
        if (!s || s === 'none') return '--';
        return s.substring(0,1).toUpperCase() + s.substring(1);
    }
    formatAllocationChanges(s: string) {
        let retValue = '--';
        switch (s) {
            case "NEVER":
                retValue = "Never";
                break;
            case "ANNUALLY":
                retValue = "+/- Annually";
                break;
            case "QUARTERLY":
                retValue = "+/- Quarterly";
                break;
            case "MONTHLY":
                retValue = "+/- Monthly";
                break;
            case "CONTINUOUS":
                retValue = "Continuous/as needed";
                break;
            case "NONE":
                retValue = "--";
                break;
            default:
                retValue = "--";
        }
        return retValue;
    }

    formatUsedFor(s: string) {
        let retValue = '--';
        switch (s.toUpperCase()) {
            case "CSTRATEGY":
                retValue = "Complete Strategy";
                break;
            case "SLEEVE":
                retValue = "Sleeve";
                break;
            case "INCOME":
                retValue = "Income";
                break;
            case "CUSTOM":
                retValue = "Custom";
                break;
            default:
                retValue = "--";
        }
        return retValue;
    }

    formatUsedForArray(arr: string[]): string[] {
        return arr.map(s => {
            switch (s.toUpperCase()) {
                case "CSTRATEGY":
                    return "Complete Strategy";
                case "SLEEVE":
                    return "Sleeve";
                case "INCOME":
                    return "Income";
                case "CUSTOM":
                    return "Custom";
                default:
                    return "--";
            }
        });
    }

    formatBooleanToYesNo(v: boolean) {
        return v ? 'Yes' : 'No';
    }

    cellRendererSelectorNumbers(params: any, currentValue: any, rendererParams: any) {
        if (params.data && params.data.usedFor === 'custom' && (currentValue == null || currentValue === 0 || currentValue === '')) {
            return {
                component: "cellRendererFormatter",
                params: { value: "--" },
            };
        } else {
            rendererParams.value = currentValue;
            return {
                component: "cellRendererFormatter",
                params: rendererParams
            };
        }
    }


    ///////////////////////////////////////////////
    /////////////// TOOLTIPS INFO /////////////////
    ///////////////////////////////////////////////

    finMasonFinScoreInfo() {
        const msg = `
            <div class="block">The FinScore is a third-party score provided by FinMason. This score attempts to measure the risk of a portfolio on a scale of 1 to 100, with 1 being the safest and 100 being the riskiest. Many people find the 1-100 scale more intuitive than some of the statistical outputs used by investment professionals. Specific points on this scale are roughly in line with how professional asset allocators classify&nbsp;risk:</div>

            <ul class="block">
              <li>FinScore 10: Conservative</li>
              <li>FinScore 30: Moderately Conservative</li>
              <li>FinScore 50: Moderate</li>
              <li>FinScore 70: Moderately Aggressive</li>
              <li>FinScore 90: Aggressive</li>
            </ul>

            <div class="block"><strong>Limitations:</strong></div>

            <ol class="block">
              <li>FinScore is calculated using the targeted security and does not factor in equivalency rules.</li>
              <li>If the FinScore value is empty, that indicates the model is targeting an unrecognized security, e.g. custom, group, SMA, etc.</li>
            </ol>

            <div>Schwab and FinMason are not affiliated.</div>
        `;
        return msg;
    }
    buildingBlockStyleInfo() {
        let msg = '<div class="block">The building block style refers to the ETFs or funds that make up this model - e.g. active, passive, smart beta, or a mix.</div>';
        msg += '<div class="block">The holdings in active funds may turn over frequently in an attempt to outperform a benchmark, while the holdings in passive funds are selected to mimic an index. Smart beta funds use a combination of both passive and active strategies while using preset and transparent rules.</div>';
        msg += '<p><strong>Please note:</strong> Individual equities are considered passive.</p>';
        return msg;
    }

    usedForInfo() {
        let msg = 'This indicates whether the manager believes this model is best suited for a complete strategy, or best when blended with another model (sleeve).';
        return msg;
    }

    isTaxManagedInfo() {
        let msg = 'If a model is classified as tax managed, the products within the model - as well as the allocation changes - are managed to maximize after-tax investment return with the intention of minimizing distributions. These models are managed in a way that makes them more suitable for taxable accounts.';
        return msg;
    }

    isSociallyResponsibleInfo() {
        let msg = '<div class="block">Socially responsible models are models containing ETFs or mutual funds that hold securities in companies that follow certain social, religious, or environmental beliefs.</div>';
        msg += '<div>Schwab does not do any due diligence on this designation; it is solely up to you, the investment manager, to ensure that the models are correctly represented.</div>';
        return msg;
    }

    riskToleranceInfo() {
        let msg = 'Typically, the risk category of the model is defined by the model\'s volatility range.';
        return msg;
    }

    weightedYieldInfo() {
        let msg = 'The weighted yield is the projected annual income to investors of the model. Only ETFs and mutual funds are considered for this calculation. This value is calculated by multiplying each security\'s yield by its respective target allocation and summing the result for all securities in the model.';
        return msg;
    }

    performanceInfo() {
        let msg = '<div class="block">Net return calculations from the performance as-of-date for the following:</div>';
        msg += '<div class="block"><em><strong>Please Note:</strong> Net returns data does not include any hypothetical or back-tested results. For models that maintain performance that would be considered hypothetical in nature, all performance numbers will show as 0 in the table.  If a model shows a 0 in the As-of Inception Return data point, that means all performance is hypothetical.</em></div>';
        msg += '<div class="block"><div>1MO &ndash; Last month</div>';
        msg += '<div>T3 &ndash; Trailing 3 months</div>';
        msg += '<div>YTD &ndash; Year to date</div>';
        msg += '<div>1YR &ndash; Trailing 1 year</div>';
        msg += '<div>3YR &ndash; Trailing 3 years</div>';
        msg += '<div>5YR &ndash; Trailing 5 years</div>';
        msg += '<div>10YR &ndash; Trailing 10 years</div></div></div>';
        msg += '<div class="block">';
        msg += '<div>As-of Inception Date</div><div>As-of Inception Return %</div></div>';

        msg += '<div>Model Manager Performance Information:</div>';
        msg += '<div class="block">Performance information displayed on the Model Market Center (MMC) is not calculated, reviewed, or verified by either Schwab or the MMC. Performance information is maintained by the model manager and entered into a third-party reporting platform for display in the MMC.</div>';
        msg += '<div class="block">Advisors are solely responsible for verifying the accuracy of any investment performance information provided through the MMC. Schwab does not recommend the use of technical analysis as a sole means of investment research. MMC is not intended to be investment advice or a recommendation of any security or model. Investing in securities can be volatile and involves risk, including loss of principal. Consider your client’s individual circumstances.</div>'

        msg += '<div class="block"><em><strong>Please Note:</strong> Performance information displayed on the MMC may differ from performance gathered from other sources depending on the methods used to calculate the performance.</em></div>'

        msg += '<div style="display:flex;padding-left:10px;"><div style="width:45px;">&ndash;</div><div>Performance in the MMC is based on the performance of the composite model that each model follows.  Performance is calculated by the model manager and entered into a third-party reporting platform for display in the MMC.</div></div>'
        msg += '<div style="display:flex;padding-left:10px;"><div style="width:45px;">&ndash;</div><div>Performance in FinMason is based on the performance of each individual holding over that given time period.  FinMason will show the longest common period based on the individual holdings and is not based off the history of the model.</div></div>'

        return msg;
    }

    turnoverdInfo() {
        let msg = 'This is the annual turnover for this model for the previous calendar year. Turnover is calculated by taking the total net buys and sells and dividing that by the portfolio (or representative account) value.';
        return msg;
    }

    allocationChangesInfo() {
        let msg = 'This is when the allocation is changed: quarterly, annually, monthly, continuous/as needed, or never.';
        return msg;
    }

    allocationLastUpdInfo() {
        let msg = 'The last updated date is the day when the model allocations, or security targets, were most recently changed.';
        return msg;
    }

    costBPsInfo() {
        let msg = 'This number represents the cost - in basis points - assessed to the advisor for using this model/product. It is otherwise known as a strategist fee. This number does not include the underlying fund expenses that an investor will pay for purchasing securities included in the model\'s allocations.';
        return msg;
    }

    weightedNetExpenseRatioInfo() {
        let msg = 'The weighted net expense ratio is the estimated cost investors are charged to be invested in the model. It is calculated by multiplying each security\'s net expense ratio by its respective target weighting and summing the result for all securities in the model.';
        return msg;
    }

    suggestedMinimumInfo() {
        let msg = 'You have the option to suggest an investment minimum for this model. This will be displayed to advisors, but the iRebal system will not force advisors to adhere to this minimum.';
        return msg;
    }

    estimatedTransactionFeeInfo() {
        let msg = 'This is the estimated transaction fee that would be incurred by an investor to implement the ETFs, exchange-listed stocks, and Mutual Funds in the model in an all-cash portfolio. This can also help you identify the models with underlying securities that have no commission fees. Note: Certain investor subaccounts, like employee retirement plans, Personal Choice Retirement Accounts (PCRAs) and Health Savings Accounts (HSAs), may be subject to holding availability and transaction fees. Advisors should confirm the fund availability before model implementation. Please contact iRebal Support at 866-640-2472 for more information.';
        return msg;
    }

    tdaDataInfo() {
        return 'Schwab is the source of this data.';
    }

    yesNoFilterData() {
        let filterData = ['Yes', 'No'];
        if (this.usedForList.includes('CUSTOM')) {
            filterData.unshift('None');
        }
        return filterData;
    }

    getColumnLimits() {
        return {
            columnLimits: [
                { key: "modelMarketName", minWidth: 225 },
                { key: "modelProviderName", minWidth: 185 },
                { key: "status", minWidth: 105 },
                { key: "updateStatus", minWidth: 112 },
                { key: "finscore", minWidth: 111 },
                { key: "buildingBlockStyle", minWidth: 165 },
                { key: "usedFor", minWidth: 139 },
                { key: "isTaxManaged", minWidth: 125 },
                { key: "isSociallyResponsible", minWidth: 172 },
                { key: "riskTolerance.label", minWidth: 212 },
                { key: "assetClass.label", minWidth: 135 },
                { key: "weightedYield", minWidth: 135 },
                { key: "currentHoldings", minWidth: 144 },
                { key: "assetType.label", minWidth: 121 },
                { key: "suggestedMinimum", minWidth: 170 },
                { key: "costBPs", minWidth: 126 },
                { key: "weightedNetExpenseRatio", minWidth: 214 },
                { key: "estimatedTransactionFee", minWidth: 204 },
                { key: "aumPerModel", minWidth: 130 },
                { key: "accountsPerModel", minWidth: 161 },
                { key: "morningStarId", minWidth: 116 },
                { key: "sourceId", minWidth: 120 },
                { key: "performanceModifiedDate", minWidth: 141 },
                { key: "modelMarketId", minWidth: 90 },
                { key: "me", minWidth: 75 },
                { key: "t3", minWidth: 75 },
                { key: "ytd", minWidth: 75 },
                { key: "t12", minWidth: 75 },
                { key: "yr3", minWidth: 75 },
                { key: "yr5", minWidth: 75 },
                { key: "yr10", minWidth: 75 },
                { key: "rorInception", minWidth: 170 },
                { key: "inceptionDate", minWidth: 153 },
                { key: "gipsCompliant", minWidth: 122 },
                { key: "performanceAsOf", minWidth: 144 },
                { key: "turnover", minWidth: 100 },
                { key: "allocationChanges", minWidth: 163 },
                { key: "publishOrAllocationUpdatedDate", minWidth: 192 }
            ]
        }
    }

    getProviderAdminFirstColumn() {
        return {
            headerName: '',
            suppressColumnsToolPanel: true,
            children: [{
                headerName: 'Select All',
                colId: 'selectAll',
                headerComponent: 'customHeader',
                cellRenderer: 'customHeader',
                cellRendererParams: {parentObject: this.mmcInventoryComponent, type: 'rowCheckBox'},
                suppressColumnsToolPanel: true,
                suppressFiltersToolPanel: true,
                suppressSizeToFit: true,
                toolPanelClass: 'toolpanel-header-checkbox-hide',
                headerComponentParams: {parentObject: this.mmcInventoryComponent, type: 'headerCheckBox'},
                floatingFilterComponent: "customHeader",
                floatingFilterComponentParams: {parentObject: this.mmcInventoryComponent, type: "NoClearAllBtn"},
                suppressFloatingFilterButton: true,
                pinned: 'left',
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                sortable: false,
                filter: true,
                resizable: true,
                minWidth: 24,
                width: 24,
                maxWidth: 24,
                cellStyle: {'padding': '5px 3px 0 2px'},
                headerClass: 'customHeaderClassSelectAll',
                cellClass: 'selectAllCheckBoxRecord'
            }]
        }
    }
}
